/**
 * Modal para Seleção de Analista/Gestor
 * 
 * Permite ao gestor selecionar analistas da mesma área para assumir OS
 * Respeita as regras de área da empresa
 * 
 * @author Equipe GestaoeCRM
 * @date 2025-01-17
 * @version 0.33.3
 */

import React, { useState, useEffect } from 'react';
import { 
  X, 
  User, 
  Users,
  Building2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { usersApi } from '@/services/api';
import LoadingSpinner from './LoadingSpinner';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'GESTOR' | 'ANALISTA';
  active: boolean;
  area?: {
    id: string;
    name: string;
  };
}

interface SelectAnalystModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (analystId: string) => void;
  title?: string;
  description?: string;
  filterWithoutArea?: boolean; // Se true, mostra apenas analistas sem área definida
}

export default function SelectAnalystModal({
  isOpen,
  onClose,
  onSelect,
  title = "Selecionar Analista",
  description = "Escolha um analista da sua área para assumir esta OS",
  filterWithoutArea = false
}: SelectAnalystModalProps) {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  
  // Estados
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAnalyst, setSelectedAnalyst] = useState<string>('');

  // Carregar usuários disponíveis quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      // console.log('🔍 DEBUG: Modal opened, loading users...');
      loadAvailableUsers();
    }
  }, [isOpen, user]);

  const loadAvailableUsers = async () => {
    try {
      setLoading(true);
      const response = await usersApi.getAll();
      // console.log('🔍 DEBUG: Users response:', response);
      
      // Verificar diferentes estruturas de resposta possíveis
      let usersData = response.data;
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        usersData = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        usersData = response.data;
      } else {
        // console.warn('⚠️ WARNING: No valid users array found in response');
        setAvailableUsers([]);
        return;
      }
      
      if (usersData && Array.isArray(usersData)) {
        // console.log('🔍 DEBUG: Total users received:', usersData.length);
        
        // Filtrar usuários conforme a necessidade
        let filteredUsers = usersData;
        
        // Se filterWithoutArea é true, mostrar apenas analistas sem área
        if (filterWithoutArea) {
          filteredUsers = usersData.filter((user: User) => {
            return user.role === 'ANALISTA' && !user.area?.id && user.active;
          });
        }
        
        setAvailableUsers(filteredUsers);
      } else {
        // console.warn('⚠️ WARNING: Users data is not an array:', response.data);
        setAvailableUsers([]);
      }
    } catch (error) {
      // console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários disponíveis');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (!selectedAnalyst) {
      toast.error('Selecione um analista');
      return;
    }
    
    // Verificar se o usuário selecionado está ativo
    const selectedUser = availableUsers.find(user => user.id === selectedAnalyst);
    if (selectedUser && !selectedUser.active) {
      toast.error('Não é possível selecionar um usuário inativo');
      return;
    }
    
    onSelect(selectedAnalyst);
    setSelectedAnalyst('');
    onClose();
  };

  // Obter cor do role
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'text-red-600 bg-red-100';
      case 'GESTOR': return 'text-blue-600 bg-blue-100';
      case 'ANALISTA': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/12 backdrop-blur-xl rounded-2xl border border-white/20 w-full max-w-2xl max-h-[90vh] flex flex-col shadow-glass">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/15 bg-white/5/80 backdrop-blur-xl flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-white leading-tight">{title}</h2>
            <p className="text-gray-300 mt-1 text-sm">{description}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" />
              <span className="ml-3 text-gray-300">Carregando usuários...</span>
            </div>
          ) : availableUsers.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                Nenhum usuário disponível
              </h3>
              <p className="text-gray-400 mb-4">
                {user?.role === 'GESTOR' 
                  ? user.area?.id 
                    ? 'Não há analistas disponíveis na sua área'
                    : 'Você precisa ter uma área definida para selecionar analistas'
                  : 'Não há usuários disponíveis para seleção'
                }
              </p>
              {user?.role === 'GESTOR' && !user.area?.id && (
                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-yellow-300 text-sm">
                    <strong>Dica:</strong> Entre em contato com o administrador para definir sua área de trabalho.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-blue-500/15 rounded-lg border border-blue-500/25 text-sm">
                <div className="flex items-center text-blue-300">
                  <Building2 className="w-4 h-4 mr-2" />
                  <span className="font-medium">
                    {user?.role === 'ADMIN' 
                      ? 'Você pode selecionar qualquer analista ou gestor ativo'
                      : user?.role === 'GESTOR'
                      ? `Usuários disponíveis: analistas ativos da sua área ou você mesmo`
                      : 'Você pode selecionar apenas a si mesmo'
                    }
                  </span>
                </div>
              </div>

              {availableUsers.map((analyst) => (
                <div 
                  key={analyst.id}
                  className={`p-4 rounded-xl border transition-all duration-200 ${
                    !analyst.active 
                      ? 'opacity-50 cursor-not-allowed bg-gray-600/10 border-gray-500/20'
                      : selectedAnalyst === analyst.id
                      ? 'bg-purple-500/20 border-purple-400/60 ring-2 ring-purple-400/60 cursor-pointer'
                      : 'bg-white/6 border-white/12 hover:bg-white/10 hover:border-white/20 cursor-pointer'
                  }`}
                  onClick={() => analyst.active && setSelectedAnalyst(analyst.id)}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`flex items-center justify-center w-5 h-5 rounded-full border-2 transition-colors ${
                        selectedAnalyst === analyst.id
                          ? 'bg-purple-500 border-purple-500'
                          : 'border-white/30'
                      }`} aria-hidden>
                        {selectedAnalyst === analyst.id && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center flex-wrap gap-3">
                          <h3 className="text-white font-semibold text-base leading-tight">{analyst.name}</h3>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleColor(analyst.role)}`}>
                            {analyst.role}
                          </span>
                          {!analyst.active && (
                            <span className="px-2 py-1 rounded text-xs font-medium text-red-600 bg-red-100">
                              INATIVO
                            </span>
                          )}
                        </div>
                        
                        <p className="text-gray-300 text-sm">{analyst.email}</p>
                        
                        {analyst.area && (
                          <div className="flex items-center text-gray-400 text-sm mt-1">
                            <Building2 className="w-3 h-3 mr-1" />
                            {analyst.area.name}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {analyst.id === user?.id && (
                      <div className="flex items-center text-blue-300 text-sm whitespace-nowrap">
                        <User className="w-4 h-4 mr-1 shrink-0" />
                        Você
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-5 border-t border-white/15 bg-white/5/80 backdrop-blur-xl flex-shrink-0">
          <div className="text-sm text-gray-300">
            {availableUsers.length} usuário(s) disponível(is)
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
            >
              Cancelar
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedAnalyst}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300"
            >
              <Users className="w-4 h-4 mr-2" />
              Selecionar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
